import { useState, useCallback } from "react";

export default function useEditorFieldHistory({
  fields,
  setFields,
  setAiBefundPrompt,
  setAiBeurteilungPrompt,
  setAiBefundHighlightRange,
  showStatus,
}) {
  const [fieldHistory, setFieldHistory] = useState({ befund: [], beurteilung: [] });
  const [fieldFuture, setFieldFuture] = useState({ befund: [], beurteilung: [] });

  const updateField = useCallback((key, val) => {
    if (key === "aiBefundPrompt") { setAiBefundPrompt(val); return; }
    if (key === "aiBeurteilungPrompt") { setAiBeurteilungPrompt(val); return; }

    setFields(f => {
      const prev = f[key];
      if ((key === "befund" || key === "beurteilung") && prev !== val) {
        setFieldHistory(h => {
          const next = [...h[key], prev];
          return { ...h, [key]: next.length > 50 ? next.slice(next.length - 50) : next };
        });
        setFieldFuture((future) => ({ ...future, [key]: [] }));
      }
      return { ...f, [key]: val };
    });
  }, [setAiBefundPrompt, setAiBeurteilungPrompt, setFields]);

  const undoField = useCallback((key) => {
    setFieldHistory(h => {
      const list = h[key] || [];
      if (!list.length) return h;
      const last = list[list.length - 1];
      const next = list.slice(0, -1);
      setFields(f => {
        const current = f[key];
        setFieldFuture((future) => {
          const nextFuture = [...future[key], current];
          return {
            ...future,
            [key]: nextFuture.length > 50 ? nextFuture.slice(nextFuture.length - 50) : nextFuture,
          };
        });
        return { ...f, [key]: last };
      });
      return { ...h, [key]: next };
    });
  }, [setFields]);

  const redoField = useCallback((key) => {
    setFieldFuture((future) => {
      const list = future[key] || [];
      if (!list.length) return future;
      const nextValue = list[list.length - 1];
      const nextFuture = list.slice(0, -1);

      setFields((f) => {
        const current = f[key];
        setFieldHistory((history) => {
          const nextHistory = [...history[key], current];
          return {
            ...history,
            [key]: nextHistory.length > 50 ? nextHistory.slice(nextHistory.length - 50) : nextHistory,
          };
        });
        return { ...f, [key]: nextValue };
      });

      return { ...future, [key]: nextFuture };
    });
  }, [setFields]);

  const applyBeurteilungWithHistory = useCallback((nextBeurteilung, statusMessage) => {
    const previousBeurteilung = fields.beurteilung;
    if (previousBeurteilung === nextBeurteilung) {
      showStatus("Keine Änderung in der Beurteilung erkannt.", true);
      return false;
    }

    setFieldHistory((history) => {
      const nextHistory = [...history.beurteilung, previousBeurteilung];
      return {
        ...history,
        beurteilung: nextHistory.length > 50 ? nextHistory.slice(nextHistory.length - 50) : nextHistory,
      };
    });
    setFieldFuture((future) => ({ ...future, beurteilung: [] }));
    setFields((prev) => ({ ...prev, beurteilung: nextBeurteilung }));
    showStatus(statusMessage);
    return true;
  }, [fields.beurteilung, setFields, showStatus]);

  const applyBefundWithHistory = useCallback((nextBefund, highlightRange, statusMessage) => {
    const previousBefund = fields.befund;
    if (previousBefund === nextBefund) {
      showStatus("Keine Änderung im Befund erkannt.", true);
      return false;
    }

    setFieldHistory((history) => {
      const nextHistory = [...history.befund, previousBefund];
      return {
        ...history,
        befund: nextHistory.length > 50 ? nextHistory.slice(nextHistory.length - 50) : nextHistory,
      };
    });
    setFieldFuture((future) => ({ ...future, befund: [] }));
    setFields((prev) => ({ ...prev, befund: nextBefund }));
    setAiBefundHighlightRange({
      start: highlightRange.start,
      end: highlightRange.end,
      nonce: Date.now(),
    });
    showStatus(statusMessage);
    return true;
  }, [fields.befund, setAiBefundHighlightRange, setFields, showStatus]);

  const resetFieldHistory = useCallback(() => {
    setFieldHistory({ befund: [], beurteilung: [] });
    setFieldFuture({ befund: [], beurteilung: [] });
  }, []);

  return {
    fieldHistory,
    fieldFuture,
    updateField,
    undoField,
    redoField,
    applyBefundWithHistory,
    applyBeurteilungWithHistory,
    resetFieldHistory,
  };
}
